/**
 * Sort FAQs by priority descending, treating a missing/blank priority as 0 so
 * unset FAQs always sort last, with an alphabetical (question) tiebreak.
 *
 * Sorts in JS rather than relying on the DB: Postgres (prod) and SQLite (local)
 * order NULLs differently under `sort=priority:desc`, so a blank priority can
 * otherwise float to the top on prod. Returns a new array; does not mutate input.
 */
export const sortFaqsByPriority = (faqs) =>
  (faqs || []).slice().sort((a, b) =>
    (b.priority ?? 0) - (a.priority ?? 0) ||
    (a.question || '').localeCompare(b.question || '')
  )

export const faqSlug = (faq) => faq?.slug || faq?.documentId || ''

export const faqAnchorId = (faq) => `faq-${faqSlug(faq)}`

const isUnsafeUrl = (url) => /^\s*(javascript|data|vbscript):/i.test(url || '')

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

export const faqAnswerToHtml = (answer) => {
  if (answer == null) { return '' }
  if (typeof answer === 'string') { return answer }

  if (Array.isArray(answer)) {
    return answer.map((block) => {
      if (block.type === 'paragraph') {
        const content = block.children?.map((child) => {
          if (child.type === 'link') {
            const linkText = child.children?.map((c) => escapeHtml(c.text)).join('') || ''
            const href = isUnsafeUrl(child.url) ? '#' : escapeHtml(child.url)
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
          }
          return escapeHtml(child.text || '')
        }).join('') || ''
        return `<p>${content}</p>`
      }
      return ''
    }).join('')
  }

  return '<p>Content format not supported. Please contact support.</p>'
}

export const toJsonLdString = (obj) => JSON.stringify(obj)
  .replace(/</g, '\\u003c')
  .replace(/>/g, '\\u003e')
  .replace(/&/g, '\\u0026')
  .replace(/\u2028/g, '\\u2028')
  .replace(/\u2029/g, '\\u2029')
